import axios from "axios";
import config from "../../api_config.json";
import {
  queryAllDocs,
  orgCoordsQuery,
  queryDocsByContent,
  querySingleDocFull,
  queryDocsByOrg,
  mutationDeleteOrgDoc,
} from "./queries";

const url = (env) => `${config.baseUrl[env]}/graphql`;
const copyUrl = (env) => `${config.baseUrl[env]}/org-docs/copy`;
const authHeaders = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

// Escapa i metacaratteri regex per il full-text (matchesRegex tratta il termine come pattern).
const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Carica documenti e coordinate in parallelo. Query plurali senza ?raw=true → array piatti.
export const fetchDocsAndCoords = async (token, env) => {
  const [docsRes, coordsRes] = await Promise.all([
    axios.post(url(env), { query: queryAllDocs }, authHeaders(token)),
    axios.post(url(env), { query: orgCoordsQuery }, authHeaders(token)),
  ]);
  return {
    docs: Array.isArray(docsRes.data) ? docsRes.data : [],
    coords: Array.isArray(coordsRes.data) ? coordsRes.data : [],
  };
};

// Documenti della sola org (query plurale → array piatto). Niente coordinate: la
// mappa è disattivata in questa vista, quindi orgLat/orgLng restano undefined.
export const fetchOrgDocs = async (token, env, orgId) => {
  const res = await axios.post(
    url(env),
    {
      query: queryDocsByOrg,
      variables: { where: { org_pointer: { have: { objectId: { equalTo: orgId } } } } },
    },
    authHeaders(token)
  );
  // Robusto a entrambi i formati del proxy: array piatto oppure edges grezzi.
  const data = res.data;
  if (Array.isArray(data)) return data;
  const edges = (data && data.org_docs && data.org_docs.edges) || [];
  return edges.map((e) => e.node);
};

// Elimina un documento e restituisce la risposta GraphQL grezza.
export const deleteOrgDoc = async (token, env, id) => {
  const res = await axios.post(
    url(env),
    { query: mutationDeleteOrgDoc, variables: { id } },
    authHeaders(token)
  );
  return res.data;
};

// Full-text server-side → array di objectId.
export const fetchDocIdsByContent = async (token, env, term) => {
  const res = await axios.post(
    url(env),
    { query: queryDocsByContent, variables: { term: escapeRegex(term) } },
    authHeaders(token)
  );
  // Robusto a entrambi i formati del proxy: array piatto oppure edges grezzi.
  const data = res.data;
  if (Array.isArray(data)) return data.map((d) => d.objectId);
  const edges = (data && data.org_docs && data.org_docs.edges) || [];
  return edges.map((e) => e.node.objectId);
};

// Documento completo (query singola → oggetto).
export const fetchFullDoc = async (token, env, id) => {
  const res = await axios.post(
    url(env),
    { query: querySingleDocFull, variables: { id } },
    authHeaders(token)
  );
  const data = res.data;
  if (data && Array.isArray(data.errors) && data.errors.length > 0) {
    throw new Error(data.errors.map((e) => e.message).filter(Boolean).join("; "));
  }
  return data?.org_doc || data?.data?.org_doc || data;
};

// Crea una copia server-side: il backend clona i campi Parse e duplica i File.
export const copyDocToOrg = async (token, env, sourceDocId, targetOrgId, nome) => {
  const res = await axios.post(
    copyUrl(env),
    { sourceDocId, targetOrgId, nome_documento: nome },
    authHeaders(token)
  );
  return res.data?.data?.org_doc;
};

// Lista province italiane dal servizio select2 (pubblico, senza auth).
export const fetchProvince = async (env, search = "") => {
  const base = config.select2Data[env];
  const res = await axios.get(`${base}/province`, {
    params: search ? { search } : {},
  });
  return res.data?.results || [];
};

// Lista regioni italiane dal servizio select2 (pubblico, senza auth).
export const fetchRegioni = async (env, search = "") => {
  const base = config.select2Data[env];
  const res = await axios.get(`${base}/regioni`, {
    params: search ? { search } : {},
  });
  return res.data?.results || [];
};

// Ricerca comuni dal servizio select2 (per la select Città della mappa).
export const fetchComuni = async (env, search) => {
  const base = config.select2Data[env];
  const res = await axios.get(`${base}/comuni`, {
    params: { search, showNations: false },
  });
  return res.data?.results || [];
};

// Geocoding via Nominatim (OpenStreetMap), limitato all'Italia. Ritorna {lat,lng} o null.
export const geocode = async (query) => {
  const res = await axios.get("https://nominatim.openstreetmap.org/search", {
    params: { q: query, format: "json", countrycodes: "it", limit: 1 },
  });
  const first = Array.isArray(res.data) && res.data[0];
  return first ? { lat: parseFloat(first.lat), lng: parseFloat(first.lon) } : null;
};
